import * as React from "react";
import { useReactToPrint } from "react-to-print";
import { jamboree2027PreviewLogo } from "../../assets";
import Heading from "../../component-lib/Atoms/heading/EventEHRHeading";
import Table from "../../component-lib/Organisms/table/EventEHRTable";
import Button from "../../component-lib/Molecules/button/Button";
import { TEXT_CONSTANTS } from "../../utils/text-constants/textConstants";
import { useDispatch, useSelector } from "react-redux";
import { componentKey } from "../../screens/patient-screen/patientSlice";
import { formatPhoneNumber } from "../../utils/phoneNumberFormatter";
import { isOffsiteTypeOfCare } from "../../utils/offsite/isOffsite";
import moment from "moment";
import { useParams } from "react-router-dom";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getSortedSceneSummeryData } from "../../screens/patient-screen/jot-form/constant";
import { getVisitNoteByIdForPreviewDischargeForm } from "../patient-screen/patientSaga";

const headerTitleFrontStyle = {
  fontSize: "14px",
  fontWeight: 600,
  color: "#393939",
  marginBottom: "10px",
};

export default function ViewDischargeForm() {
  const commonHeadingStyle = { color: "#004981", marginBottom: "16px" };

  const accordionBgColor = {
    backgroundColor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    borderRadius: "12px",
    marginBottom: "16px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  };
  const fullWidth = { width: "100%" };

  // Get the last part of the URL
  const { visitId, visitNoteId } = useParams();

  const { visitNoteCareFlowDataForDischargePreview: visitNoteCareFlowData } =
    useSelector((state: any) => state[componentKey]);

  const indexId = visitNoteCareFlowData?.visit_note?.findIndex(
    (obj: any) => obj.id === visitNoteId
  );

  let index = indexId;
  // Set index to 0 if not found
  if (index === -1) {
    index = visitNoteCareFlowData?.visit_note?.length - 1;
  }

  const emsSceneData: any = getSortedSceneSummeryData(
    visitNoteCareFlowData?.visit_note?.[index],
    visitNoteCareFlowData
  );

  const { demographics, initialData, soapAndProc, iftSoapNote } =
    emsSceneData || {};

  const {
    IVPlacement,
    Lead12EKG,
    MedicationAdministered,
    Other,
    Oxygen,
    airwayManagement,
    woundCare_dressing,
    Splinting,
  } = soapAndProc?.procedureDone || {};

  const { endotrachealIntubation, iCGEL } = airwayManagement || {};

  const intubationData = endotrachealIntubation;
  const iCGELData = iCGEL;

  const {
    sentToHospitalByAir,
    sentToHospitalByPOV,
    sentToHospitalByEMS,
    sentToHospitalByNSJTransportation,
  } = emsSceneData?.disposition?.dispositionData?.dispositions || {};

  // Visit note data
  const visitNote = visitNoteCareFlowData?.visit_note?.[index] || {};

  // Interval notes aggregated across all visit notes (they may sit on an
  // earlier "InInterval" note rather than the discharged one).
  const intervalNotes = (visitNoteCareFlowData?.visit_note || [])
    .flatMap((vn: any) => vn?.intervalNotes || [])
    .sort(
      (a: any, b: any) =>
        new Date(a?.createdAt).getTime() - new Date(b?.createdAt).getTime()
    );
  const intervalNotesColumns = [
    {
      field: "srNo",
      label: <div></div>,
      renderLogic: (_row: any, i: any) => (
        <p className="text-neutral-90 font-normal">{i + 1}</p>
      ),
    },
    {
      field: "note",
      label: <div className="w-[40vw]">Note</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.note || "-"}</p>
      ),
    },
    {
      field: "dateTime",
      label: <div>Date &amp; Time</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">
          {row?.createdAt
            ? moment(row?.createdAt).format("MM/DD/YYYY hh:mm A")
            : "-"}
        </p>
      ),
    },
  ];
  const rxORowData = visitNoteCareFlowData?.visit_note?.[index]?.order?.filter(
    (data: any) => data?.type === "Rx Order"
  );
  const noneRXRowData = visitNoteCareFlowData?.visit_note?.[
    index
  ]?.order?.filter((data: any) => data?.type !== "Rx Order");
  const sections = [
    { key: "historyOfPresent", title: "History Of Present Illness" },
    { key: "objectiveNote", title: "Objective Note" },
    { key: "assessmentDescription", title: "Assessment Description" },
    { key: "plan", title: "Plan" },
    { key: "subjectiveNote", title: "Subjective Note" },
    { key: "severity", title: "Severity" },
    { key: "objectiveSymptoms", title: "Objective Symptoms" },
    { key: "description", title: "Description" },
  ];

  const dispatch = useDispatch();

  const getIntubationStatus = (successful?: boolean) => {
    return successful === true ? "Yes" : successful === false ? "No" : "-";
  };
  // GET TRUTH KEY
  const getTrueKey = (route: Record<string, boolean>) => {
    return (
      Object.entries(route).find(([_, value]) => value === true)?.[0] ?? "-"
    );
  };

  const getModifiedString = (text: string) => {
    if (text) {
      return (
        text?.charAt(0)?.toUpperCase() +
        text?.substring(1)?.split("_")?.join(" ")
      );
    }
    return "-";
  };

  const patient = visitNoteCareFlowData?.patient?.participant;
  const headerTitleFrontStyle = {
    fontSize: "14px",
    fontWeight: 600,
    color: "#393939",
    marginBottom: "10px",
  };
  const labelFontStyle = {
    fontWeight: 500,
    color: "#727272",
    fontSize: "12px",
    marginRight: "8px",
    minWidth: "120px",
    display: "inline-block",
  };
  const valueFontStyle = {
    fontWeight: 500,
    color: "#393939",
    fontSize: "12px",
    marginLeft: "8px",
  };
  const colonFontStyle = {
    fontWeight: "400",
    fontSize: "12px",
    color: "#393939",
    marginRight: "8px",
  };

  const patientDetails = [
    {
      label: "Patient Name",
      value: `${patient?.firstName || "-"} ${patient?.lastName || ""}`,
    },
    { label: "BSA ID", value: patient?.bsaId || "-" },
    {
      label: "Date of Visit",
      value: visitNoteCareFlowData?.createdAt
        ? moment(visitNoteCareFlowData?.createdAt)?.format("MM/DD/YYYY")
        : "-",
    },
    {
      label: "Contact Number",
      value: patient?.contactNumber
        ? formatPhoneNumber(patient?.contactNumber)
        : "-",
    },
    { label: "Unit Number", value: patient?.baseCamp?.unitNumber || "-" },
    {
      label: "Treating Clinic",
      value:
        visitNoteCareFlowData?.visit_note?.[index]?.createdProvider?.facility
          ?.name ||
        visitNoteCareFlowData?.patient?.facility?.name ||
        "-",
    },
  ];

  if (visitNoteCareFlowData?.type === "Scene") {
    patientDetails.push(
      {
        label: "Unit Type",
        value: initialData?.unitType || "-",
      },
      {
        label: "Unit Designation",
        value: initialData?.unitDesignation || "-",
      },

      {
        label: "Date Of Birth",
        value: demographics?.dob
          ? // DOB is date-only (midnight UTC) — render in UTC so it never shifts.
            moment.utc(demographics?.dob).format("MM/DD/YYYY")
          : "",
      },
      {
        label: "Age",
        value: demographics?.age || "-",
      },
      {
        label: "Gender",
        value: demographics?.gender || "-",
      },
      {
        label: "Registration Id",
        value: demographics?.registrationId || "-",
      },
      {
        label: "Participant Type",
        value: demographics?.participantType || "-",
      }
    );
  }

  const incidentDetails = [
    {
      label: "Incident Date",
      value: visitNoteCareFlowData?.incidentDate
        ? // Date-only value (midnight UTC) — render in UTC so it never shifts.
          moment.utc(visitNoteCareFlowData?.incidentDate)?.format("MM/DD/YYYY")
        : "-",
    },
    {
      label: "Incident Time",
      value: visitNoteCareFlowData?.incidentTime || "-",
    },
    {
      label: "Incident Location",
      value: visitNoteCareFlowData?.incidentlocation || "-",
    },
    { label: "Witness Name", value: visitNoteCareFlowData?.witnessName || "-" },
    {
      label: "Witness Email",
      value: visitNoteCareFlowData?.witnessEmail || "-",
    },
  ];

  if (visitNoteCareFlowData?.type === "Scene") {
    incidentDetails.push({
      label: "Witness Contact Number",
      value: visitNoteCareFlowData?.witnessContact
        ? formatPhoneNumber(visitNoteCareFlowData?.witnessContact)
        : "-",
    });
  }

  if (visitNoteCareFlowData?.type === "Scene") {
    patientDetails.push({
      label: "Participant Type",
      value: demographics?.participantType || "-",
    });
  }

  const ICDCodeColumns = [
    {
      field: "srNo",
      label: <div>ICD 10 Code</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.code}</p>;
      },
    },
    {
      field: "details",
      label: <div className="">Details</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.desc}</p>;
      },
    },
  ];

  // RX ORDER COLUMNS
  const RXOrderColumns = [
    {
      field: "srNo",
      label: <div></div>,
      renderLogic: (row: any, index: any) => {
        return <p className="text-neutral-90 font-normal">{index + 1}</p>;
      },
    },
    {
      field: "medication",
      label: <div className="w-[10vw]">Medication</div>,
      renderLogic: (row: any) => {
        return <p className=" font-normal">{row?.item?.[0]?.medicationName}</p>;
      },
    },
    {
      field: "qty",
      label: <div className="w-[5vw]">Qty</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">
            {row?.item?.[0]?.quantity}
          </p>
        );
      },
    },
    {
      field: "unit",
      label: <div className="w-[5vw]">Unit</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">{row?.item?.[0]?.unit}</p>
        );
      },
    },
    {
      field: "details",
      label: <div className="w-[20vw]">Details</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.note}</p>;
      },
    },
    {
      field: "status",
      label: <div className="w-[10vw]">Status</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">
            {row?.status ? row?.status?.split("_")?.join(" ") : "-"}
          </p>
        );
      },
    },
  ];
  // CKO ORDER COLUMNS
  const nonRXColumns = [
    {
      field: "srNo",
      label: <div></div>,
      renderLogic: (row: any, index: any) => {
        return <p className="text-neutral-90 font-normal">{index + 1}</p>;
      },
    },
    {
      field: "orderType",
      label: <div className="w-[10vw]">Order Type</div>,
      renderLogic: (row: any) => {
        return (
          <p className="font-normal">
            {row?.type?.split("_")?.join(" ") || "-"}
          </p>
        );
      },
    },
    {
      field: "Items",
      label: <div className="w-[10vw]">Items</div>,
      renderLogic: (row: any) => {
        return <p className="font-normal">{row?.item?.[0]?.name || "-"}</p>;
      },
    },
    {
      field: "details",
      label: <div className="w-[50vw]">Details</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.note}</p>;
      },
    },
    {
      field: "status",
      label: <div className="w-[10vw]">Status</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">
            {row?.status ? row?.status?.split("_")?.join(" ") : "-"}
          </p>
        );
      },
    },
  ];

  const vitalsColumns = [
    {
      field: "Pulse",
      label: <div>Pulse</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.pulse}</p>;
      },
    },
    {
      field: "SBP",
      label: <div className="">SBP</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.SBP}</p>;
      },
    },
    {
      field: "DBP",
      label: <div className="">DBP</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.DBP}</p>;
      },
    },
    {
      field: "Resp Rate",
      label: <div>Resp Rate</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">{row?.respirationRate}</p>
        );
      },
    },

    {
      field: "SpO2",
      label: <div>SpO2</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.SPO2}</p>;
      },
    },
    {
      field: "GCS",
      label: <div className="">GCS</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.GCS}</p>;
      },
    },
    {
      field: "Time",
      label: <div className="">Time</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.time}</p>;
      },
    },
  ];

  const iVInsertionColumns = [
    {
      field: "Size",
      label: <div>Size</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.size}</p>;
      },
    },
    {
      field: "Attempts",
      label: <div className="">Attempts</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.attempts}</p>;
      },
    },
    {
      field: "Successful",
      label: <div className="">Successful</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.successful}</p>;
      },
    },
    {
      field: "Time",
      label: <div>Time</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.time}</p>;
      },
    },
  ];

  const medicationAdministeredColumns = [
    {
      field: "Medication",
      label: <div>Medication</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.medication}</p>;
      },
    },
    {
      field: "Dose (mg)",
      label: <div className="">Dose (mg)</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.dose}</p>;
      },
    },
    {
      field: "Route",
      label: <div className="">Route</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.route}</p>;
      },
    },
    {
      field: "Response",
      label: <div>Response</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.response}</p>;
      },
    },
    {
      field: "Time",
      label: <div>Time</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.time}</p>;
      },
    },
  ];

  const oxygenColumns = [
    {
      field: "Device",
      label: <div>Device</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.device}</p>;
      },
    },
    {
      field: "LPM",
      label: <div className="">LPM</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.LPM}</p>;
      },
    },
    {
      field: "Improvement",
      label: <div>Improvement</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">{row?.improvement}</p>
        );
      },
    },
    {
      field: "Time",
      label: <div>Time</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.time}</p>;
      },
    },
  ];

  const iGelInsertionColumn = [
    {
      field: "Size",
      label: <div>Size</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.size}</p>;
      },
    },
    {
      field: "Attempts",
      label: <div className="">Attempts</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.attempts}</p>;
      },
    },
    {
      field: "Successful",
      label: <div className="">successful</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.successful}</p>;
      },
    },
    {
      field: "Time",
      label: <div>Time</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.time}</p>;
      },
    },
  ];

  const endIntColumn = [
    {
      field: "Attempts",
      label: <div>Attempts</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.attempts}</p>;
      },
    },
    {
      field: "Blade Size",
      label: <div className="">Blade Size</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.bladeSize}</p>;
      },
    },
    {
      field: "Device",
      label: <div className="">Device</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.device}</p>;
      },
    },
    {
      field: "Tube Size",
      label: <div className="">Tube Size</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.tubeSize}</p>;
      },
    },
    {
      field: "Successful",
      label: <div className="">successful</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.successful}</p>;
      },
    },
    {
      field: "Time",
      label: <div>Time</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.time}</p>;
      },
    },
  ];

  const endIntRowData = [
    {
      attempts: intubationData?.attempts || "-",
      bladeSize: intubationData?.bladeSize || "-",
      device: intubationData?.device || "-",
      tubeSize: intubationData?.tubeSize || "-",
      successful: getIntubationStatus(intubationData?.successful),
      time: intubationData?.time || "-",
    },
  ];

  const iGelInsertionRowData = [
    {
      size: iCGELData?.size || "-",
      attempts: iCGELData?.attempts || "-",
      successful: getIntubationStatus(iCGELData?.successful),
      time: iCGELData?.time || "-",
    },
  ];

  const vitalsRowData = emsSceneData?.soapAndProc?.vitals?.map(
    (data: any, index: number) => ({
      pulse: data?.heartRate || "-",
      SBP: data?.systolic || "-",
      DBP: data?.diastolic || "-",
      respirationRate: data?.respirationRate || "-",
      SPO2: data?.oxygenSaturation || "-",
      GCS: data?.GCS || "-",
      time: data?.time || "-",
    })
  );

  const oxygenRowData = Oxygen?.[0]?.IVs?.map((data: any, index: number) => ({
    device: data?.device || "-",
    LPM: data?.LPM || "-",
    DBP: data?.DBP || "-",
    improvement: data?.improvement || "-",
    time: data?.time || "-",
  }));

  const medicationAdministeredColumnsRowData =
    MedicationAdministered?.[0]?.meds?.map((data: any, index: number) => ({
      medication: data?.medication || "-",
      dose: data?.dose || "-",
      route: getTrueKey(data?.route),
      response: data?.response?.Improve
        ? "Improve"
        : data?.response?.nochange
          ? "No Change"
          : data?.response?.worsen
            ? "Worsen"
            : "-",
      time: data?.response?.time || "-",
    }));

  const iVInsertionRowData = IVPlacement?.[0]?.data?.map(
    (data: any, index: number) => ({
      size: data?.size || "-",
      attempts: data?.attempts || "-",
      successful: data?.successful || "-",
      time: data?.time || "-",
    })
  );

  const contentRef = React.useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({ contentRef });

  React.useEffect(() => {
    dispatch(getVisitNoteByIdForPreviewDischargeForm(visitId));
  }, []);

  const dischargeNote =
    visitNoteCareFlowData?.visit_note?.[index].dischargeNote;
  const rowData = visitNoteCareFlowData?.visit_note?.[index]?.assessment;

  return (
    <React.Fragment>
      <style>
        {`
          @media print {
            .print-content {
              width: 100% !important;
              max-width: none !important;
              margin: 0 !important;
              padding: 0 !important;
              background-color: white !important;
              font-size: 12pt !important;
            }
            
            /* Hide elements that shouldn't print */
            .no-print-button {
              display: none !important;
            }
            
            /* Ensure tables print properly */
            table {
              width: 100% !important;
              page-break-inside: avoid !important;
            }
            
            /* Ensure headers don't break across pages */
            h1, h2, h3, h4, h5, h6 {
              page-break-after: avoid !important;
            }
            
            /* Ensure accordions are expanded for printing */
            .MuiAccordion-root {
              box-shadow: none !important;
            }
            
            .MuiAccordionDetails-root {
              display: block !important;
            }
            
            /* Remove background colors and shadows for better printing */
            .MuiAccordion-root,
            .MuiAccordionSummary-root {
              background: white !important;
              box-shadow: none !important;
            }
          }
        `}
      </style>

      <div
        ref={contentRef}
        style={{
          width: "60vw",
          margin: "0 auto",
          height: "-webkit-fill-available",
          overflow: "auto",
          backgroundColor: "#f7fafc",
          minHeight: "100dvh",
          maxWidth: "1200px",
        }}
        className="print-content"
      >
        {/* Enhanced Header */}
        <header className="flex items-center justify-between p-6 bg-white shadow-sm">
          <div className="flex items-center gap-3">
            <img
              src={jamboree2027PreviewLogo}
              alt="jamboree logo"
              className="h-12 w-auto"
            />
            <div className="font-weight-900 font-size-20px color-393939">
              JAMBOREE 2026
            </div>
          </div>

          <div className="font-weight-650 font-size-18px color-005596">
            JamboCare
          </div>
        </header>

        {/* Enhanced Type Badge */}
        <div className="background-005596 color-FFFFFF font-weight-500 font-size-14px text-align-center p-2 mx-6 mt-4 rounded-md">
          {visitNoteCareFlowData?.typeOfCare === "Self_Care"
            ? "Self Care"
            : visitNoteCareFlowData?.typeOfCare
                  ?.toLowerCase()
                  ?.includes("nurse")
              ? "Nurse Care"
              : visitNoteCareFlowData?.typeOfCare
                    ?.toLowerCase()
                    ?.includes("mental")
                ? "Mental Health Care"
                : visitNoteCareFlowData?.typeOfCare
                      ?.toLowerCase()
                      ?.includes("ear")
                  ? "Listening Ear Care"
                  : visitNoteCareFlowData?.typeOfCare === "Physician"
                    ? "Physician Care"
                    : visitNoteCareFlowData?.typeOfCare === null
                      ? "EMS Scene "
                      : visitNoteCareFlowData?.typeOfCare}{" "}
          Visit
        </div>

        {/* Enhanced Patient Details */}
        <div className="pt-6 pl-9 pb-6 pr-6 bg-white mx-6 mt-4 rounded-lg shadow-sm">
          <Heading
            title="Patient Details"
            customStyle={headerTitleFrontStyle}
          />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "12px",
              alignItems: "start",
            }}
          >
            {patientDetails?.map((data, index) => (
              <div
                key={index}
                style={{ display: "flex", alignItems: "baseline" }}
              >
                <span style={labelFontStyle}>{data?.label}</span>
                <span style={colonFontStyle}>:</span>
                <span style={valueFontStyle}>{data?.value}</span>
              </div>
            ))}
          </div>
        </div>

        <hr className="mx-6 my-4" />

        {/* Enhanced Incident Details */}
        <div className="pt-6 pl-9 pb-6 pr-6 bg-white mx-6 rounded-lg shadow-sm">
          <Heading
            title="Incident Details"
            customStyle={headerTitleFrontStyle}
          />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "12px",
              alignItems: "start",
            }}
          >
            {incidentDetails?.map((data, index) => (
              <div
                key={index}
                style={{ display: "flex", alignItems: "baseline" }}
              >
                <span style={labelFontStyle}>{data?.label}</span>
                <span style={colonFontStyle}>:</span>
                <span style={valueFontStyle}>{data?.value}</span>
              </div>
            ))}
          </div>
          {visitNoteCareFlowData?.type === "Scene" && (
            <div className="pt-6 pb-4 pr-4">
              <Heading
                title="BRIEF factual description of incident:"
                customStyle={labelFontStyle}
              />
              <div
                style={{ ...valueFontStyle, marginLeft: 0 }}
                className="font-weight-500 color-979797 font-size-12px mt-3 p-3 bg-gray-50 rounded-md"
              >
                {visitNoteCareFlowData?.visit_note?.[index]?.chiefComplaints ||
                  "-"}
              </div>
            </div>
          )}
        </div>

        <hr className="mx-6 my-4" />

        {/* Enhanced Chief Complaint */}
        {visitNoteCareFlowData?.type !== "Scene" && (
          <div className="pt-6 pl-9 pb-6 pr-6 bg-white mx-6 rounded-lg shadow-sm">
            <Heading
              title="Chief Complaint"
              customStyle={headerTitleFrontStyle}
            />
            <div className="font-weight-500 color-979797 font-size-12px p-3 bg-gray-50 rounded-md">
              {visitNoteCareFlowData?.chiefComplaints}
            </div>
          </div>
        )}

        <hr className="mx-6 my-4" />

        {/* Enhanced Visit Note Sections */}
        <div className="mx-6">
          {sections?.map(
            ({ key, title }) =>
              visitNote[key] && (
                <div key={key} className="bg-white rounded-lg shadow-sm mb-4">
                  <VisitNoteSection title={title} content={visitNote[key]} />
                </div>
              )
          )}
        </div>

        {/* Interval Notes (aggregated across the visit's notes) */}
        {intervalNotes?.length > 0 && (
          <div className="pt-6 pl-9 pb-6 pr-6 bg-white mx-6 mt-4 rounded-lg shadow-sm important-content">
            <Heading title="Interval Notes" customStyle={headerTitleFrontStyle} />
            <div className="overflow-x-auto">
              <Table columns={intervalNotesColumns} rows={intervalNotes} />
            </div>
          </div>
        )}
        {/* Encounter Topic & Resolution — Listening Ear only */}
        {visitNoteCareFlowData?.typeOfCare?.toLowerCase()?.includes("ear") && (
          <div className="mx-6">
            {visitNote?.encounterTopic?.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm mb-4 pt-6 pl-9 pb-6 pr-6">
                <Heading
                  title="Encounter Topic"
                  customStyle={headerTitleFrontStyle}
                />
                <div
                  className="p-3 bg-gray-50 rounded-md"
                  style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}
                >
                  {visitNote.encounterTopic.map((topic: string) => (
                    <span
                      key={topic}
                      style={{
                        backgroundColor: "#EAF2FB",
                        color: "#005596",
                        borderRadius: "16px",
                        padding: "4px 12px",
                        fontSize: "12px",
                        fontWeight: 500,
                        WebkitPrintColorAdjust: "exact",
                        printColorAdjust: "exact",
                      }}
                    >
                      {topic}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {visitNote?.resolution && (
              <div className="bg-white rounded-lg shadow-sm mb-4 pt-6 pl-9 pb-6 pr-6">
                <Heading
                  title="Resolution"
                  customStyle={headerTitleFrontStyle}
                />
                <div className="font-weight-500 color-979797 font-size-12px p-3 bg-gray-50 rounded-md leading-relaxed">
                  {visitNote.resolution}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Enhanced Diagnosis — hidden for Listening Ear and Offsite Transfers */}
        {!visitNoteCareFlowData?.typeOfCare?.toLowerCase()?.includes("ear") &&
          !isOffsiteTypeOfCare(visitNoteCareFlowData?.typeOfCare) && (
            <div className="pt-6 pl-9 pb-6 pr-6 bg-white mx-6 mt-4 rounded-lg shadow-sm important-content">
              <Heading title="Diagnosis" customStyle={headerTitleFrontStyle} />
              <div className="overflow-x-auto">
                <Table columns={ICDCodeColumns} rows={rowData} />
              </div>
            </div>
          )}

        {/* Enhanced Discharge Instructions */}
        {dischargeNote && (
          <div className="pt-6 pl-9 pb-6 pr-6 bg-white mx-6 mt-4 rounded-lg shadow-sm important-content">
            <Heading
              title="Discharge Instructions"
              customStyle={headerTitleFrontStyle}
            />
            <div
              className="font-weight-500 color-979797 font-size-12px p-4 bg-green-50 border border-green-200 rounded-md"
              style={{
                lineHeight: "1.6",
                whiteSpace: "pre-wrap",
                color: "black",
                fontFamily: "inter",
              }}
            >
              {dischargeNote}
            </div>
          </div>
        )}

        {/* Enhanced Rx Order */}
        {rxORowData?.length > 0 && (
          <div className="pt-6 pl-9 pb-6 pr-6 bg-white mx-6 mt-4 rounded-lg shadow-sm important-content">
            <Heading
              title="Rx Order"
              customStyle={{
                fontSize: "16px",
                fontWeight: 500,
                color: "#393939",
                marginBottom: "12px",
              }}
            />
            <div className="overflow-x-auto">
              <Table
                columns={RXOrderColumns}
                rows={rxORowData}
                noDataFoundMessage={"No ICD 10 or DSM Code entered"}
              />
            </div>
          </div>
        )}

        {/* Enhanced Non Rx Order */}
        {noneRXRowData?.length > 0 && (
          <div className="pt-6 pl-9 pb-6 pr-6 bg-white mx-6 mt-4 rounded-lg shadow-sm important-content">
            <Heading
              title="Non Rx Order"
              customStyle={{
                fontSize: "16px",
                fontWeight: 500,
                color: "#393939",
                marginBottom: "12px",
              }}
            />
            <div className="overflow-x-auto">
              <Table
                columns={nonRXColumns}
                rows={noneRXRowData}
                noDataFoundMessage={"No Data"}
              />
            </div>
          </div>
        )}

        {/* Enhanced Accordions */}
        {visitNoteCareFlowData?.type === "Scene" &&
          !visitNoteCareFlowData?.visit_note?.[index]?.assessment && (
            <div className="mt-8 mx-6 flex flex-col gap-4">
              {/* SOAP and Procedures */}
              {visitNoteCareFlowData?.unitType !==
                "Military FLA-Interfacility Transport_ONLY" && (
                <Accordion
                  className="important-content"
                  expanded
                  sx={{
                    ...accordionBgColor,
                    "& .MuiAccordionSummary-root": {
                      backgroundColor: "rgba(102, 126, 234, 0.1)",
                      borderRadius: "8px 8px 0 0",
                    },
                    "& .MuiAccordionDetails-root": {
                      padding: "24px",
                    },
                  }}
                  style={fullWidth}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <span className="font-semibold text-gray-800">
                      SOAP and Procedures
                    </span>
                  </AccordionSummary>
                  <AccordionDetails sx={{ backgroundColor: "white" }}>
                    {/* Vital Signs Section */}
                    <div className="p-3">
                      <div className="w-[60%] pb-3">
                        <Heading
                          title="Chief Complaint"
                          customStyle={headerTitleFrontStyle}
                        />
                        <div className="font-weight-500 color-979797 font-size-12px">
                          {emsSceneData?.soapAndProc?.chiefComplaints}
                        </div>
                      </div>

                      <hr />

                      <div className="pt-5 pb-3">
                        <Heading
                          title="Subjective"
                          customStyle={headerTitleFrontStyle}
                        />
                        <div className="font-weight-500 color-979797 font-size-12px">
                          {emsSceneData?.soapAndProc?.subjectiveNote}
                        </div>
                      </div>

                      <hr />

                      <div className="mt-5">
                        <Heading
                          title="Vital Signs - Please Scroll To see all fields"
                          customStyle={headerTitleFrontStyle}
                        />
                        <Table
                          columns={vitalsColumns}
                          rows={vitalsRowData}
                          noDataFoundMessage={"No Data"}
                        />
                      </div>
                    </div>

                    <hr />

                    <div className="pt-5 pb-3">
                      <Heading
                        title="Objective/Exam Findings"
                        customStyle={headerTitleFrontStyle}
                      />
                      <div className="font-weight-500 color-979797 font-size-12px">
                        {emsSceneData?.soapAndProc?.objectiveNote}
                      </div>
                    </div>
                    <hr />

                    <div className="pt-5  pb-3">
                      <Heading
                        title="Assessment and Plan & Treatment"
                        customStyle={headerTitleFrontStyle}
                      />
                      <div className="font-weight-500 color-979797 font-size-12px">
                        {emsSceneData?.soapAndProc?.assessment}
                      </div>
                    </div>
                    <hr />

                    <div className="pt-5">
                      <Heading
                        title="Procedures Done"
                        customStyle={headerTitleFrontStyle}
                      />

                      <div className="flex flex-col gap-2">
                        {airwayManagement?.select && (
                          <div className="font-weight-500 color-979797 font-size-12px">
                            Airway Management
                          </div>
                        )}
                        {woundCare_dressing?.select && (
                          <div className="font-weight-500 color-979797 font-size-12px">
                            Wound Care/Dressing
                          </div>
                        )}
                        {IVPlacement?.[0]?.select && (
                          <div className="font-weight-500 color-979797 font-size-12px">
                            IV Placement
                          </div>
                        )}
                        {MedicationAdministered?.[0]?.select && (
                          <div className="font-weight-500 color-979797 font-size-12px">
                            Medication Administered
                          </div>
                        )}

                        {Lead12EKG?.select && (
                          <div className="font-weight-500 color-979797 font-size-12px">
                            12 Lead EKG
                          </div>
                        )}
                        {Splinting?.select && (
                          <div className="font-weight-500 color-979797 font-size-12px">
                            Splinting
                          </div>
                        )}
                        {Oxygen?.[0]?.select && (
                          <div className="font-weight-500 color-979797 font-size-12px">
                            Oxygen
                          </div>
                        )}
                        {Other?.select && (
                          <div className="font-weight-500 color-979797 font-size-12px">
                            Other
                          </div>
                        )}
                        {Other?.select && (
                          <div className="mt-5 pb-3 ">
                            <Heading
                              title="Other Procedure Done"
                              customStyle={headerTitleFrontStyle}
                            />
                            <div className="font-weight-500 color-979797 font-size-12px">
                              {Other?.description || "-"}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <hr />

                    {/* If Airway Management Selected */}
                    {airwayManagement?.select && (
                      <div className="pt-5 pb-3">
                        <Heading
                          title="Airway Management"
                          customStyle={headerTitleFrontStyle}
                        />

                        <div className="flex flex-col gap-2">
                          {airwayManagement?.bvm && (
                            <div className="font-weight-500 color-979797 font-size-12px">
                              BVM
                            </div>
                          )}
                          {airwayManagement?.suctioning && (
                            <div className="font-weight-500 color-979797 font-size-12px">
                              Suctioning
                            </div>
                          )}
                          {airwayManagement?.OPA && (
                            <div className="font-weight-500 color-979797 font-size-12px">
                              OPA
                            </div>
                          )}
                          {airwayManagement?.NPA && (
                            <div className="font-weight-500 color-979797 font-size-12px">
                              NPA
                            </div>
                          )}

                          {iCGEL?.select && (
                            <div className="font-weight-500 color-979797 font-size-12px">
                              iGel
                            </div>
                          )}
                          {endotrachealIntubation?.select && (
                            <div className="font-weight-500 color-979797 font-size-12px">
                              Endotracheal Intubation
                            </div>
                          )}
                          {airwayManagement?.other?.select && (
                            <div className="font-weight-500 color-979797 font-size-12px">
                              Other
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    <hr />

                    {/* AM other */}
                    {airwayManagement?.select &&
                      airwayManagement?.other?.select && (
                        <>
                          <div className="mt-5 pb-3">
                            <Heading
                              title="Other Airway Management"
                              customStyle={headerTitleFrontStyle}
                            />
                            <div className="flex gap-8 mb-2">
                              <div className="font-weight-500 color-979797 font-size-12px">
                                {airwayManagement?.other?.desription || "-"}
                              </div>
                            </div>
                          </div>
                          <hr />
                        </>
                      )}

                    {/* AM Endotracheal Intubation */}
                    {airwayManagement?.select &&
                      endotrachealIntubation?.select && (
                        <>
                          <div className="mt-5">
                            <Heading
                              title="Endotracheal Intubation"
                              customStyle={headerTitleFrontStyle}
                            />

                            <Table
                              columns={endIntColumn}
                              rows={endIntRowData}
                              noDataFoundMessage={"No Data"}
                            />
                          </div>
                          <hr />
                        </>
                      )}

                    {/* AM iGel Insertion */}
                    {airwayManagement?.select && iCGEL?.select && (
                      <>
                        <div className="mt-5 pb-3">
                          <Heading
                            title="iGel Insertion"
                            customStyle={headerTitleFrontStyle}
                          />
                          <Table
                            columns={iGelInsertionColumn}
                            rows={iGelInsertionRowData}
                            noDataFoundMessage={"No Data"}
                          />
                        </div>
                        <hr />
                      </>
                    )}

                    {/* AM IV Insertion
                     */}
                    {IVPlacement?.[0]?.select && (
                      <>
                        <div className="mt-5">
                          <Heading
                            title="IV Insertion"
                            customStyle={headerTitleFrontStyle}
                          />
                          <Table
                            columns={iVInsertionColumns}
                            rows={iVInsertionRowData}
                            noDataFoundMessage={"No Data"}
                          />
                        </div>
                        <hr />
                      </>
                    )}

                    {/* Medications Administered
                     */}
                    {MedicationAdministered?.[0]?.select && (
                      <>
                        <div className="mt-5">
                          <Heading
                            title="Medication Administered"
                            customStyle={headerTitleFrontStyle}
                          />

                          <Table
                            columns={medicationAdministeredColumns}
                            rows={medicationAdministeredColumnsRowData}
                            noDataFoundMessage={"No Data"}
                          />
                        </div>
                        <hr />
                      </>
                    )}

                    {/* Oxygen Administration
                     */}
                    {Oxygen?.[0]?.select && (
                      <>
                        <div className="mt-5 pb-5">
                          <Heading
                            title="Oxygen Administration"
                            customStyle={headerTitleFrontStyle}
                          />

                          <Table
                            columns={oxygenColumns}
                            rows={oxygenRowData}
                            noDataFoundMessage={"No Data"}
                          />
                        </div>
                        <hr />
                      </>
                    )}

                    {Lead12EKG?.select && (
                      <div className="pt-5">
                        <Heading
                          title="12 Lead Interpretation"
                          customStyle={headerTitleFrontStyle}
                        />
                        <div className="font-weight-500 color-979797 font-size-12px">
                          {emsSceneData?.soapAndProc?.procedureDone?.Lead12EKG
                            ?.lead12Interpration || "-"}
                        </div>
                      </div>
                    )}
                  </AccordionDetails>
                </Accordion>
              )}

              {/* IFT SOAP Note */}
              {visitNoteCareFlowData?.unitType ===
                "Military FLA-Interfacility Transport_ONLY" && (
                <Accordion
                  className="important-content"
                  expanded
                  sx={accordionBgColor}
                  style={fullWidth}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    IFT SOAP Note
                  </AccordionSummary>
                  <AccordionDetails sx={{ backgroundColor: "white" }}>
                    <div>
                      <div className="pt-5">
                        <Heading
                          title="Please include a brief SOAP note of your transport, including vital signs, if necessary."
                          customStyle={headerTitleFrontStyle}
                        />

                        <div className="font-weight-500 color-979797 font-size-12px">
                          {iftSoapNote}
                        </div>
                      </div>
                    </div>
                  </AccordionDetails>
                </Accordion>
              )}

              {/*   Diagnosis and Disposition
               */}
              <Accordion
                className="important-content"
                expanded
                sx={accordionBgColor}
                style={fullWidth}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  Diagnosis and Disposition{" "}
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: "white" }}>
                  <div className="p-3">
                    <div className="pt-5 pb-3">
                      <Heading
                        title="Impression/Diagnosis"
                        customStyle={headerTitleFrontStyle}
                      />
                      <div className="font-weight-500 color-979797 font-size-12px">
                        {Array.isArray(
                          emsSceneData?.disposition?.dispositionData
                            ?.impressionDiagnosis
                        )
                          ? emsSceneData?.disposition?.dispositionData?.impressionDiagnosis?.join(
                              ", "
                            )
                          : getModifiedString(
                              emsSceneData?.disposition?.dispositionData
                                ?.impressionDiagnosis
                            )}
                      </div>
                    </div>
                    <hr />

                    <div className="pt-5">
                      <Heading
                        title="Transferring Unit"
                        customStyle={headerTitleFrontStyle}
                      />
                      <div className="font-weight-500 color-979797 font-size-12px">
                        {getModifiedString(
                          emsSceneData?.disposition?.dispositionData
                            ?.transferringUnit
                        )}
                      </div>
                    </div>
                  </div>
                </AccordionDetails>
              </Accordion>

              {/* Treat and Release */}
              {emsSceneData?.disposition?.dispositionData?.dispositions
                ?.treatedAndReleased?.select && (
                <Accordion
                  className="important-content"
                  expanded
                  sx={accordionBgColor}
                  style={fullWidth}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    Treat and Release
                  </AccordionSummary>
                  <AccordionDetails sx={{ backgroundColor: "white" }}>
                    <div className="p-3 text-[#004981]">
                      <h3 style={headerTitleFrontStyle}>
                        I, the treating EMS Clinician, certify that this
                        individual is greater that 18 years of age, or in the
                        care of someone 18 years of age or older. If more than
                        scout rendered first aid was provided AND the patient is
                        a minor with no adult is present, the scout must be
                        taken to a medical facility to be evaluated and released
                        by a physician.
                      </h3>
                      <div className="pt-3"></div>
                    </div>
                    <div className="pt-5 pb-5">
                      <hr />
                    </div>

                    {true && (
                      <div className="p-3 text-[#004981]">
                        <h3 style={headerTitleFrontStyle}>
                          Neither I nor the attending EMT(s) and/or paramedic(s)
                          believe that I, my child, or the scout I am
                          supervising have an illness or injury requiring EMS
                          transport to a Jamboree Medical Facility. I agree the
                          the provided care is sufficient for the injury, as we
                          understand it at this time. I agree that the patient
                          does not need further EMS assessment, treatment or
                          transportation at this time. I understand that if I
                          change my mind or if my condition changes and I wish
                          further treatment / transportation by the Emergency
                          Medical Services (EMS) system; I can call the SOC for
                          assistance or present at any Jamboree Medical
                          Facility.Neither I nor the attending EMT(s) and/or
                          paramedic(s) believe that I, my child, or the scout I
                          am supervising have an illness or injury requiring EMS
                          transport to a Jamboree Medical Facility. I agree the
                          the provided care is sufficient for the injury, as we
                          understand it at this time. I agree that the patient
                          does not need further EMS assessment, treatment or
                          transportation at this time. I understand that if I
                          change my mind or if my condition changes and I wish
                          further treatment / transportation by the Emergency
                          Medical Services (EMS) system; I can call the SOC for
                          assistance or present at any Jamboree Medical
                          Facility.
                        </h3>

                        <div className="w-[30%] pt-7">
                          <Heading
                            title="Treat and Release Signature"
                            customStyle={commonHeadingStyle}
                          />
                          <div className="pt-3 pb-4 pr-4 w-max">
                            <div
                              style={{
                                display: "flex, border:'1px solid red",
                                background: "#F4F4F4",
                                padding: "10px 20px",
                              }}
                              className="text-center border-radius-8px"
                            >
                              <div
                                style={{
                                  fontSize: "32px",
                                  fontFamily: "Sacramento",
                                }}
                              >
                                {
                                  emsSceneData?.disposition?.dispositionData
                                    ?.dispositions?.treatedAndReleased?.name
                                }
                              </div>
                              <div className="font-weight-500 font-size-12px color-565656 ">
                                Signature
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Refusal of Medical Aid/Transport */}
              {emsSceneData?.disposition?.dispositionData?.dispositions
                ?.refusalOfMedicalAid && (
                <Accordion
                  className="important-content"
                  expanded
                  sx={accordionBgColor}
                  style={fullWidth}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    Refusal of Medical Aid/Transport
                  </AccordionSummary>
                  <AccordionDetails sx={{ backgroundColor: "white" }}>
                    <div className="p-3 text-[#004981]">
                      <h3 style={headerTitleFrontStyle}>
                        I hereby acknowledge and release the above-mentioned
                        emergency service provider and its personnel, Summit
                        Bechtel Reserve, the BSA, and all EMS system physicians,
                        nurses, agents and personnel of any liability for my
                        refusal to accept further treatment. I acknowledge that
                        I have been advised of and understand the risks of not
                        accepting emergency treatment and/or transportation to
                        the nearest hospital facility, which I am refusing. I
                        have been advised as follows: - That I should receive
                        emergency medical treatment and transportation to a
                        hospital, which I am refusing. - That having received
                        emergency medical treatment, I am refusing further aid
                        or transport to a medical facility. - I am refusing all
                        medical assessment, treatment and transport. - That my
                        failure to seek treatment and transport could result in
                        permanent injury, impairment, disability and could lead
                        to my death. - I certify that I have the ability and
                        appreciate the consequences of making decisions
                        regarding my medical treatment and the ability to reach
                        an informed decision. - I have been made aware and
                        understand that I can call Emergency Medical Services
                        again at any time and/or seek out medical care at a
                        Jamboree Medical Facility or other community medical
                        facility in the area.
                      </h3>

                      <div className="w-[30%] pt-7">
                        <Heading
                          title="RMA Release:"
                          customStyle={headerTitleFrontStyle}
                        />
                        <div className="pt-3 pb-4 pr-4">
                          <div
                            style={{
                              display: "flex, border:'1px solid red",
                              background: "#F4F4F4",
                              padding: "10px 20px",
                            }}
                            className="text-center border-radius-8px"
                          >
                            <div
                              style={{
                                fontSize: "32px",
                                fontFamily: "Sacramento",
                              }}
                            >
                              {
                                emsSceneData?.disposition?.refusalOfMedicalAid
                                  ?.rmaRelease
                              }
                            </div>
                            <div className="font-weight-500 font-size-12px color-565656 ">
                              Signature
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <hr />
                    <div>
                      <div className="w-[30%] pt-7">
                        <Heading
                          title="Witness Signature"
                          customStyle={headerTitleFrontStyle}
                        />
                        <div className="pt-3 pb-4 pr-4">
                          <div
                            style={{
                              display: "flex, border:'1px solid red",
                              background: "#F4F4F4",
                              padding: "10px 20px",
                            }}
                            className="text-center border-radius-8px"
                          >
                            <div
                              style={{
                                fontSize: "32px",
                                fontFamily: "Sacramento",
                              }}
                            >
                              {
                                emsSceneData?.disposition?.refusalOfMedicalAid
                                  ?.witnessName
                              }
                            </div>
                            <div className="font-weight-500 font-size-12px color-565656 ">
                              Signature
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Off Site Transfer */}

              {(sentToHospitalByAir ||
                sentToHospitalByPOV ||
                sentToHospitalByEMS ||
                sentToHospitalByNSJTransportation) && (
                <Accordion
                  className="important-content"
                  expanded
                  sx={accordionBgColor}
                  style={fullWidth}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    Off Site Transfer{" "}
                  </AccordionSummary>
                  <AccordionDetails sx={{ backgroundColor: "white" }}>
                    <div className="w-[30%] pt-3 pb-3">
                      <Heading
                        title="Hospital/Off Site Facility"
                        customStyle={headerTitleFrontStyle}
                      />
                      <div className="font-weight-500 color-979797 font-size-12px">
                        {getModifiedString(
                          emsSceneData?.disposition?.hospitalName
                        )}
                      </div>
                    </div>

                    <hr />
                    <div className="w-[30%] pt-5">
                      <Heading
                        title="Transferring Unit"
                        customStyle={headerTitleFrontStyle}
                      />
                      <div className="font-weight-500 color-979797 font-size-12px">
                        {getModifiedString(
                          emsSceneData?.disposition?.transferringUnit
                        )}
                      </div>
                    </div>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Signature */}
              <Accordion
                className="important-content"
                expanded
                sx={accordionBgColor}
                style={fullWidth}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  Signature
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: "white" }}>
                  <div>
                    <div className="pt-5">
                      <Heading
                        title="Time Call End"
                        customStyle={headerTitleFrontStyle}
                      />
                      <div className="font-weight-500 color-979797 font-size-12px">
                        {emsSceneData?.signature?.timeCallEnd || "-"}
                      </div>
                    </div>

                    <div className="pt-5">
                      <Heading
                        title="Primary Treating Clinician"
                        customStyle={headerTitleFrontStyle}
                      />
                      <div className="font-weight-500 color-979797 font-size-12px">
                        {getModifiedString(
                          emsSceneData?.signature?.primaryClinic
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="w-[30%] pt-7">
                    <Heading
                      title="Signature"
                      customStyle={headerTitleFrontStyle}
                    />
                    <div className="pt-3 pb-4 pr-4">
                      <div
                        style={{
                          display: "flex, border:'1px solid red",
                          background: "#F4F4F4",
                          padding: "10px 20px",
                        }}
                        className="text-center border-radius-8px"
                      >
                        <div
                          style={{
                            fontSize: "32px",
                            fontFamily: "Sacramento",
                          }}
                        >
                          {emsSceneData?.signature?.signature || "-"}{" "}
                        </div>
                        <div className="font-weight-500 font-size-12px color-565656 ">
                          Signature
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="pt-3 pb-5">
                    <hr />
                  </div>
                </AccordionDetails>
              </Accordion>
            </div>
          )}

        {visitNoteCareFlowData?.visit_note?.[index]?.isVisitSign && (
          <div className="pt-6 pl-9 pb-6 pr-6 bg-white mx-6 mt-4 rounded-lg shadow-sm text-end flex justify-end important-content">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                background: "#F4F4F4",
                padding: "16px 24px",
                borderRadius: "8px",
                border: "1px solid #E5E7EB",
                minWidth: "200px",
              }}
              className="text-center"
            >
              <div style={{ fontSize: "32px", fontFamily: "Sacramento" }}>
                {visitNoteCareFlowData?.visit_note?.[index]?.signedBy
                  ?.firstName +
                  " " +
                  visitNoteCareFlowData?.visit_note?.[index]?.signedBy
                    ?.lastName}
              </div>
              <div className="font-weight-500 font-size-12px color-565656 mt-2">
                Signature
              </div>
            </div>
          </div>
        )}
        {visitNoteCareFlowData?.visit_note?.[index]?.isVisitSign && (
          <div className="flex justify-end pr-6 mt-6 mb-6 no-print-button">
            <Button
              label={TEXT_CONSTANTS?.PRINT}
              onClickCb={() => reactToPrintFn()}
              variant="outlined"
              style={{
                padding: "8px 16px",
                borderRadius: "6px",
                border: "1px solid #005596",
                color: "#005596",
                backgroundColor: "white",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s ease",
                // "&:hover": {
                //   backgroundColor: "#005596",
                //   color: "white",
                // },
              }}
            />
          </div>
        )}
      </div>
    </React.Fragment>
  );
}

const VisitNoteSection = ({
  title,
  content,
}: {
  title: string;
  content: string;
}) => (
  <div className="pt-6 pl-9 pb-6 pr-6">
    <Heading title={title} customStyle={headerTitleFrontStyle} />
    <div className="font-weight-500 color-979797 font-size-12px p-3 bg-gray-50 rounded-md leading-relaxed">
      {content?.charAt(0)?.toUpperCase() + content?.substring(1)}
    </div>
  </div>
);
